// src/pages/DashboardTCC.jsx
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useRelatorioExport } from '../services/backendExportService';

// WIDGETS
import KpiCards from '../components/Dashboard/widgets/KpiCards';
import GraficoEvolucao from '../components/Dashboard/widgets/GraficoEvolucao';
import TopCursos from '../components/Dashboard/widgets/TopCursos';
import AlertasRisco from '../components/Dashboard/widgets/AlertasRisco';
import StatusMonografias from '../components/Dashboard/widgets/StatusMonografias';
import VolumeDefesas from '../components/Dashboard/widgets/VolumeDefesas';
import TempoMedio from '../components/Dashboard/widgets/TempoMedio';
import ProjetosPendentes from '../components/Dashboard/widgets/ProjetosPendentes';
import ComparativoCursos from '../components/Dashboard/widgets/ComparativoCursos';
import AnaliseAtrasos from '../components/Dashboard/widgets/AnaliseAtrasos';
import PlagioIA from '../components/Dashboard/widgets/PlagioIA';
import ProdutividadeDepartamento from '../components/Dashboard/widgets/ProdutividadeDepartamento';
import TaxaConclusao from '../components/Dashboard/widgets/TaxaConclusao';

// ===== EXPORTS DISPONÍVEIS NO BACKEND =====
const EXPORTAVEIS = new Set([
  '/relatorios-tcc/graduados',
  '/relatorios-tcc/produtividade',
  '/relatorios-tcc/atrasos',
  '/relatorios-tcc/plagio-ia'
]);

// ------- Componentes básicos -------
const Card = ({ title, children, size, style, ...props }) => (
  <div
    {...props}
    style={{
      border: '1px solid #d9d9d9',
      borderRadius: '6px',
      padding: size === 'small' ? '12px' : '16px',
      backgroundColor: 'white',
      boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
      marginBottom: '8px',
      ...style
    }}
  >
    {title && (
      <h3
        style={{
          margin: '0 0 16px 0',
          fontSize: '16px',
          color: '#1890ff',
          borderBottom: '1px solid #f0f0f0',
          paddingBottom: '8px'
        }}
      >
        {title}
      </h3>
    )}
    {children}
  </div>
);
Card.displayName = 'Card';
Card.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  children: PropTypes.node,
  size: PropTypes.string,
  style: PropTypes.object
};

const Row = ({ children, gutter, justify, align, style, ...props }) => (
  <div
    {...props}
    style={{
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: justify || 'flex-start',
      alignItems: align || 'stretch',
      gap: gutter ? `${Array.isArray(gutter) ? gutter[0] : gutter}px` : '16px',
      ...style
    }}
  >
    {children}
  </div>
);
Row.displayName = 'Row';
Row.propTypes = {
  children: PropTypes.node,
  gutter: PropTypes.oneOfType([PropTypes.array, PropTypes.number]),
  justify: PropTypes.string,
  align: PropTypes.string,
  style: PropTypes.object
};

const Col = ({ children, span, style, ...props }) => (
  <div
    {...props}
    style={{
      flex: span ? `0 0 ${(span / 24) * 100}%` : '1',
      minWidth: '0',
      ...style
    }}
  >
    {children}
  </div>
);
Col.displayName = 'Col';
Col.propTypes = {
  children: PropTypes.node,
  span: PropTypes.number,
  style: PropTypes.object
};

const Select = ({ placeholder, style, value, onChange, options, allowClear, ...props }) => (
  <select
    {...props}
    style={{
      padding: '8px 12px',
      border: '1px solid #d9d9d9',
      borderRadius: '6px',
      fontSize: '14px',
      backgroundColor: 'white',
      cursor: 'pointer',
      outline: 'none',
      ...style
    }}
    value={value || ''}
    onChange={(e) => onChange && onChange(e.target.value || null)}
  >
    <option value="">{placeholder}</option>
    {options &&
      options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
  </select>
);
Select.displayName = 'Select';
Select.propTypes = {
  placeholder: PropTypes.string,
  style: PropTypes.object,
  value: PropTypes.string,
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string
    })
  ),
  allowClear: PropTypes.bool
};

const Alert = ({ message, description, type, showIcon, ...props }) => (
  <div
    {...props}
    style={{
      padding: '16px',
      border: '1px solid #d9d9d9',
      borderRadius: '6px',
      backgroundColor: type === 'info' ? '#e6f7ff' : '#f6f8fa',
      color: '#333',
      borderLeft: `4px solid ${type === 'info' ? '#1890ff' : '#52c41a'}`
    }}
  >
    <div style={{ display: 'flex', alignItems: 'flex-start' }}>
      {showIcon && <span style={{ marginRight: '8px', fontSize: '16px' }}>ℹ️</span>}
      <div>
        <strong style={{ display: 'block', marginBottom: '4px' }}>{message}</strong>
        {description && <p style={{ margin: 0, color: '#666' }}>{description}</p>}
      </div>
    </div>
  </div>
);
Alert.displayName = 'Alert';
Alert.propTypes = {
  message: PropTypes.string,
  description: PropTypes.string,
  type: PropTypes.string,
  showIcon: PropTypes.bool
};

const Tabs = ({ children, defaultActiveKey, ...props }) => {
  const [activeKey, setActiveKey] = useState(defaultActiveKey);

  const TabsComponent = () => (
    <div {...props}>
      <div
        style={{
          borderBottom: '2px solid #f0f0f0',
          marginBottom: '24px',
          display: 'flex',
          gap: '4px'
        }}
      >
        {React.Children.map(children, (child) => (
          <button
            key={child.props.tabKey}
            onClick={() => setActiveKey(child.props.tabKey)}
            style={{
              padding: '12px 20px',
              border: 'none',
              background: activeKey === child.props.tabKey ? '#1890ff' : 'transparent',
              color: activeKey === child.props.tabKey ? 'white' : '#666',
              cursor: 'pointer',
              borderRadius: '6px 6px 0 0',
              fontSize: '14px',
              fontWeight: activeKey === child.props.tabKey ? 'bold' : 'normal',
              transition: 'all 0.2s',
              outline: 'none'
            }}
            onMouseOver={(e) => {
              if (activeKey !== child.props.tabKey) {
                e.target.style.backgroundColor = '#f0f0f0';
              }
            }}
            onMouseOut={(e) => {
              if (activeKey !== child.props.tabKey) {
                e.target.style.backgroundColor = 'transparent';
              }
            }}
          >
            {child.props.tab}
          </button>
        ))}
      </div>

      <div>{React.Children.toArray(children).find((child) => child.props.tabKey === activeKey)}</div>
    </div>
  );

  return <TabsComponent />;
};
Tabs.displayName = 'Tabs';
Tabs.propTypes = {
  children: PropTypes.node,
  defaultActiveKey: PropTypes.string
};

const TabPane = ({ children, tabKey, tab, ...props }) => (
  <div {...props} data-tab-key={tabKey} data-tab={tab}>
    {children}
  </div>
);
TabPane.displayName = 'TabPane';
TabPane.propTypes = {
  children: PropTypes.node,
  tabKey: PropTypes.string,
  tab: PropTypes.node
};

Tabs.TabPane = TabPane;

// ===== BOTÕES DE EXPORTAÇÃO (com validação de Content-Type) =====
const BotoesExportacao = ({ endpoint, filtros, titulo }) => {
  const { exportarRelatorio, exportando, progresso, erro, limparErro } = useRelatorioExport();

  const handleExportar = async (formato) => {
    try {
      console.log('🔥 BOTÃO CLICADO - Formato:', formato);
      console.log('🔥 Endpoint:', endpoint);
      console.log('🔥 Filtros:', filtros);
      
      const resultado = await exportarRelatorio(endpoint, filtros, formato, titulo);
      
      console.log('✅ Exportação concluída:', resultado);
      
      if (resultado.success) {
        console.log(`✅ ${formato.toUpperCase()} exportado com sucesso!`);
      }
      
    } catch (error) {
      console.error('❌ Erro na exportação:', error);
      alert(`Erro ao exportar ${formato}: ${error.message}`);
    }
  };

  return (
    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
      <button
        onClick={() => handleExportar('pdf')}
        disabled={exportando}
        title="Exportar como PDF"
        style={{
          padding: '4px 10px',
          fontSize: '11px',
          border: '1px solid #1890ff',
          backgroundColor: exportando ? '#f0f0f0' : '#1890ff',
          color: exportando ? '#999' : 'white',
          borderRadius: '4px',
          cursor: exportando ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s'
        }}
        onMouseOver={(e) => {
          if (!exportando) e.target.style.backgroundColor = '#096dd9';
        }}
        onMouseOut={(e) => {
          if (!exportando) e.target.style.backgroundColor = '#1890ff';
        }}
      >
        📄 PDF
      </button>

      <button
        onClick={() => handleExportar('excel')}
        disabled={exportando}
        title="Exportar como Excel"
        style={{
          padding: '4px 10px',
          fontSize: '11px',
          border: '1px solid #52c41a',
          backgroundColor: exportando ? '#f0f0f0' : '#52c41a',
          color: exportando ? '#999' : 'white',
          borderRadius: '4px',
          cursor: exportando ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s'
        }}
        onMouseOver={(e) => {
          if (!exportando) e.target.style.backgroundColor = '#389e0d';
        }}
        onMouseOut={(e) => {
          if (!exportando) e.target.style.backgroundColor = '#52c41a';
        }}
      >
        📊 Excel
      </button>

      <button
        onClick={() => handleExportar('word')}
        disabled={exportando}
        title="Exportar como Word"
        style={{
          padding: '4px 10px',
          fontSize: '11px',
          border: '1px solid #722ed1',
          backgroundColor: exportando ? '#f0f0f0' : '#722ed1',
          color: exportando ? '#999' : 'white',
          borderRadius: '4px',
          cursor: exportando ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s'
        }}
        onMouseOver={(e) => {
          if (!exportando) e.target.style.backgroundColor = '#531dab';
        }}
        onMouseOut={(e) => {
          if (!exportando) e.target.style.backgroundColor = '#722ed1';
        }}
      >
        📝 Word
      </button>

      <button
        onClick={() => handleExportar('json')}
        disabled={exportando}
        title="Exportar dados como JSON"
        style={{
          padding: '4px 8px',
          fontSize: '11px',
          border: '1px solid #faad14',
          backgroundColor: exportando ? '#f0f0f0' : '#faad14',
          color: exportando ? '#999' : 'white',
          borderRadius: '4px',
          cursor: exportando ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s'
        }}
      >
        📋
      </button>

      {exportando && (
        <div style={{ fontSize: '10px', color: '#666', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <div
            style={{
              width: '10px',
              height: '10px',
              border: '2px solid #f3f3f3',
              borderTop: '2px solid #1890ff',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}
          ></div>
          {progresso}%
        </div>
      )}
      
      {erro && (
        <div style={{ fontSize: '10px', color: '#ff4d4f', cursor: 'pointer' }} onClick={limparErro} title="Clique para limpar">
          ❌ {erro.substring(0, 30)}...
        </div>
      )}
    </div>
  );
};

BotoesExportacao.propTypes = {
  endpoint: PropTypes.string.isRequired,
  filtros: PropTypes.object,
  titulo: PropTypes.string
};

BotoesExportacao.defaultProps = {
  filtros: {},
  titulo: 'relatorio'
};
// ===== PÁGINA =====
const DashboardTCC = () => {
  const [filtros, setFiltros] = useState({
    periodo: '12',
    curso: null,
    startDate: null,
    endDate: null
  });

  const [relatorioAtivo, setRelatorioAtivo] = useState('volume-defesas');
  const [formatoGrafico, setFormatoGrafico] = useState({
    volume: 'bar',
    tempo: 'grafico',
    comparativo: 'grafico',
    atrasos: 'grafico',
    plagio: 'combinado',
    produtividade: 'grafico',
    taxa: 'grafico'
  });

  const renderVisaoGeral = () => (
    <div>
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={24}>
          <KpiCards endpoint="/dashboard-tcc/widget/kpis-principais" filtros={filtros} />
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={12}>
          <Card
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>📈 Evolução Temporal</span>
                {/* sem export: rota não possui /export no backend */}
              </div>
            }
            size="small"
          >
            <GraficoEvolucao endpoint="/dashboard-tcc/evolucao-temporal" filtros={filtros} />
          </Card>
        </Col>
        <Col span={12}>
          <Card
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>🏆 Top Cursos</span>
                {/* sem export: rota não possui /export no backend */}
              </div>
            }
            size="small"
          >
            <TopCursos endpoint="/dashboard-tcc/widget/ranking-cursos" filtros={filtros} />
          </Card>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <Card
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>📊 Status Monografias</span>
                {/* sem export: rota não possui /export no backend */}
              </div>
            }
            size="small"
          >
            <StatusMonografias endpoint="/relatorios-tcc/status" filtros={filtros} />
          </Card>
        </Col>
        <Col span={8}>
          <Card title="⚠️ Alertas de Risco" size="small">
            <AlertasRisco endpoint="/dashboard-tcc/widget/alertas-ativos" filtros={filtros} />
          </Card>
        </Col>
        <Col span={8}>
          <Card title="📈 Métricas Rápidas" size="small">
            <TempoMedio endpoint="/relatorios-tcc/tempo-medio" filtros={filtros} formato="cards" />
          </Card>
        </Col>
      </Row>
    </div>
  );

  const renderRelatorios = () => {
    const relatoriosDisponiveis = [
      {
        key: 'volume-defesas',
        label: '📊 Volume de Defesas',
        endpoint: '/relatorios-tcc/volume-defesas',
        component: VolumeDefesas,
        formatoOpcoes: [
          { value: 'bar', label: 'Barras' },
          { value: 'line', label: 'Linha' }
        ]
      },
      {
        key: 'ranking-cursos',
        label: '🏆 Ranking de Cursos',
        endpoint: '/relatorios-tcc/ranking-cursos',
        component: TopCursos
      },
      {
        key: 'tempo-medio',
        label: '⏱️ Tempo Médio',
        endpoint: '/relatorios-tcc/tempo-medio',
        component: TempoMedio,
        formatoOpcoes: [
          { value: 'grafico', label: 'Gráfico' },
          { value: 'cards', label: 'Cards' }
        ]
      },
      {
        key: 'atrasos',
        label: '⏰ Análise de Atrasos',
        endpoint: '/relatorios-tcc/atrasos',
        component: AnaliseAtrasos,
        formatoOpcoes: [
          { value: 'grafico', label: 'Gráfico' },
          { value: 'lista', label: 'Lista' },
          { value: 'pizza', label: 'Pizza' }
        ]
      },
      {
        key: 'plagio-ia',
        label: '🔍 Plágio e IA',
        endpoint: '/relatorios-tcc/plagio-ia',
        component: PlagioIA,
        formatoOpcoes: [
          { value: 'combinado', label: 'Combinado' },
          { value: 'lista', label: 'Lista' }
        ]
      },
      {
        key: 'projetos-pendentes',
        label: '📋 Projetos Pendentes',
        endpoint: '/relatorios-tcc/projetos-pendentes',
        component: ProjetosPendentes
      },
      {
        key: 'comparativo-cursos',
        label: '📈 Comparativo de Cursos',
        endpoint: '/relatorios-tcc/comparativo-cursos',
        component: ComparativoCursos,
        formatoOpcoes: [
          { value: 'grafico', label: 'Gráfico' },
          { value: 'tabela', label: 'Tabela' }
        ]
      }
    ];

    const relatorioSelecionado = relatoriosDisponiveis.find((r) => r.key === relatorioAtivo);
    const ComponenteRelatorio = relatorioSelecionado?.component;

    return (
      <Row gutter={16}>
        <Col span={6}>
          <Card title="📋 Relatórios Disponíveis" size="small">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {relatoriosDisponiveis.map((relatorio) => (
                <div
                  key={relatorio.key}
                  onClick={() => setRelatorioAtivo(relatorio.key)}
                  onKeyDown={(e) => e.key === 'Enter' && setRelatorioAtivo(relatorio.key)}
                  role="button"
                  tabIndex={0}
                  style={{
                    padding: '12px',
                    cursor: 'pointer',
                    borderRadius: '6px',
                    backgroundColor: relatorioAtivo === relatorio.key ? '#1890ff' : '#f8f8f8',
                    color: relatorioAtivo === relatorio.key ? 'white' : '#333',
                    transition: 'all 0.2s',
                    fontSize: '13px',
                    border: relatorioAtivo === relatorio.key ? '2px solid #096dd9' : '1px solid #e8e8e8'
                  }}
                >
                  {relatorio.label}
                </div>
              ))}
            </div>
          </Card>
        </Col>

        <Col span={18}>
          <Card
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>{relatorioSelecionado?.label}</span>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  {relatorioSelecionado?.formatoOpcoes && (
                    <Select
                      value={formatoGrafico[relatorioAtivo] || relatorioSelecionado.formatoOpcoes[0]?.value}
                      onChange={(valor) => setFormatoGrafico((prev) => ({ ...prev, [relatorioAtivo]: valor }))}
                      options={relatorioSelecionado.formatoOpcoes}
                      style={{ width: 120, fontSize: '12px' }}
                    />
                  )}
                  {EXPORTAVEIS.has(relatorioSelecionado?.endpoint) && (
                    <BotoesExportacao
                      endpoint={relatorioSelecionado.endpoint}
                      filtros={filtros}
                      titulo={relatorioSelecionado?.key || 'relatorio'}
                    />
                  )}
                </div>
              </div>
            }
            size="small"
          >
            {ComponenteRelatorio ? (
              <ComponenteRelatorio
                endpoint={relatorioSelecionado.endpoint}
                filtros={filtros}
                formato={formatoGrafico[relatorioAtivo]}
                tipo={formatoGrafico[relatorioAtivo]}
                limite={20}
              />
            ) : (
              <div
                style={{
                  padding: '32px 20px',
                  textAlign: 'center',
                  backgroundColor: '#fafafa',
                  borderRadius: '8px',
                  border: '2px dashed #d9d9d9'
                }}
              >
                <div style={{ marginBottom: '12px', fontSize: '24px' }}>📊</div>
                <h4 style={{ margin: '0 0 8px 0', color: '#666', fontSize: '16px' }}>Relatório em desenvolvimento</h4>
                <p style={{ margin: 0, color: '#999', fontSize: '12px' }}>
                  Endpoint: {relatorioSelecionado?.endpoint || 'N/A'}
                </p>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    );
  };

  const renderAlertas = () => (
    <div>
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={12}>
          <Card
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>🚨 Alertas de Risco</span>
                {/* sem export nesta rota */}
              </div>
            }
            size="small"
          >
            <AlertasRisco endpoint="/dashboard-tcc/widget/alertas-ativos" filtros={filtros} />
          </Card>
        </Col>
        <Col span={12}>
          <Card title="📋 Projetos Pendentes" size="small">
            <ProjetosPendentes endpoint="/relatorios-tcc/projetos-pendentes" filtros={filtros} limite={10} />
          </Card>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={12}>
          <Card
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>⏰ Análise de Atrasos</span>
                {EXPORTAVEIS.has('/relatorios-tcc/atrasos') && (
                  <BotoesExportacao endpoint="/relatorios-tcc/atrasos" filtros={filtros} titulo="atrasos" />
                )}
              </div>
            }
            size="small"
          >
            <AnaliseAtrasos endpoint="/relatorios-tcc/atrasos" filtros={filtros} formato="lista" />
          </Card>
        </Col>
        <Col span={12}>
          <Card
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>🔍 Plágio e IA</span>
                {EXPORTAVEIS.has('/relatorios-tcc/plagio-ia') && (
                  <BotoesExportacao endpoint="/relatorios-tcc/plagio-ia" filtros={filtros} titulo="plagio-ia" />
                )}
              </div>
            }
            size="small"
          >
            <PlagioIA endpoint="/relatorios-tcc/plagio-ia" filtros={filtros} formato="lista" />
          </Card>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={24}>
          <Card title="📊 Monitor de Performance" size="small">
            <Alert
              message="Sistema de Monitoramento Ativo"
              description="Monitoramento contínuo de prazos, qualidade e progresso dos TCCs. Todos os sistemas estão funcionando normalmente."
              type="info"
              showIcon
            />
          </Card>
        </Col>
      </Row>
    </div>
  );

  const renderGestao = () => (
    <div>
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={12}>
          <Card
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>📈 Produtividade por Departamento</span>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <Select
                    value={formatoGrafico.produtividade || 'grafico'}
                    onChange={(valor) => setFormatoGrafico((prev) => ({ ...prev, produtividade: valor }))}
                    options={[
                      { value: 'grafico', label: 'Gráfico' },
                      { value: 'tabela', label: 'Tabela' },
                      { value: 'radar', label: 'Radar' }
                    ]}
                    style={{ width: 100, fontSize: '12px' }}
                  />
                  {EXPORTAVEIS.has('/relatorios-tcc/produtividade') && (
                    <BotoesExportacao
                      endpoint="/relatorios-tcc/produtividade"
                      filtros={filtros}
                      titulo="produtividade-departamento"
                    />
                  )}
                </div>
              </div>
            }
            size="small"
          >
            <ProdutividadeDepartamento
              endpoint="/relatorios-tcc/produtividade-departamento"
              filtros={filtros}
              formato={formatoGrafico.produtividade}
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>📊 Taxa de Conclusão</span>
                <Select
                  value={formatoGrafico.taxa || 'grafico'}
                  onChange={(valor) => setFormatoGrafico((prev) => ({ ...prev, taxa: valor }))}
                  options={[
                    { value: 'grafico', label: 'Gráfico' },
                    { value: 'lista', label: 'Lista' },
                    { value: 'pizza', label: 'Pizza' }
                  ]}
                  style={{ width: 100, fontSize: '12px' }}
                />
              </div>
            }
            size="small"
          >
            <TaxaConclusao endpoint="/relatorios-tcc/taxa-conclusao-curso" filtros={filtros} formato={formatoGrafico.taxa} />
          </Card>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={24}>
          <Card title="📈 Comparativo Departamental" size="small">
            <ComparativoCursos endpoint="/relatorios-tcc/comparativo-departamental" filtros={filtros} formato="tabela" />
          </Card>
        </Col>
      </Row>
    </div>
  );

  return (
    <div
      style={{
        padding: '24px',
        backgroundColor: '#f0f2f5',
        minHeight: '100vh',
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      }}
    >
      <style>{`
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
      `}</style>

      <Card style={{ marginBottom: 24 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <h1
              style={{
                margin: '0 0 8px 0',
                color: '#1890ff',
                fontSize: '28px',
                fontWeight: 'bold'
              }}
            >
              📄 Dashboard TCC - Monografias
            </h1>
            <p style={{ margin: 0, color: '#666', fontSize: '16px' }}>Visão analítica e relatórios do sistema de TCCs</p>
          </Col>
          <Col>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <Select
                placeholder="Selecionar Curso"
                style={{ width: 220 }}
                value={filtros.curso}
                onChange={(curso) => setFiltros({ ...filtros, curso })}
                options={[
                  { label: 'Engenharia de Software', value: 'Engenharia de Software' },
                  { label: 'Administração de Sistemas', value: 'Administração de Sistemas' },
                  { label: 'Engenharia de Computação', value: 'Engenharia de Computação' }
                ]}
              />
              <Select
                placeholder="Período"
                value={filtros.periodo}
                style={{ width: 180 }}
                onChange={(periodo) => setFiltros({ ...filtros, periodo })}
                options={[
                  { label: 'Último mês', value: '1' },
                  { label: 'Últimos 3 meses', value: '3' },
                  { label: 'Últimos 6 meses', value: '6' },
                  { label: 'Último ano', value: '12' },
                  { label: 'Últimos 2 anos', value: '24' }
                ]}
              />
              {EXPORTAVEIS.has('/relatorios-tcc/graduados') && (
                <BotoesExportacao endpoint="/relatorios-tcc/graduados" filtros={filtros} titulo="graduados" />
              )}
            </div>
          </Col>
        </Row>
      </Card>

      <Card>
        <Tabs defaultActiveKey="visao-geral">
          <Tabs.TabPane tab="📊 Visão Geral" tabKey="visao-geral">
            {renderVisaoGeral()}
          </Tabs.TabPane>

          <Tabs.TabPane tab="📄 Relatórios" tabKey="relatorios">
            {renderRelatorios()}
          </Tabs.TabPane>

          <Tabs.TabPane tab="⚠️ Alertas" tabKey="alertas">
            {renderAlertas()}
          </Tabs.TabPane>

          <Tabs.TabPane tab="⚙️ Gestão" tabKey="gestao">
            {renderGestao()}
          </Tabs.TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

DashboardTCC.displayName = 'DashboardTCC';
export default DashboardTCC;
